// https://base-ui.com/react/components/tooltip
import { Tooltip as BaseTooltip, type TooltipRootChangeEventDetails } from "@base-ui/react/tooltip";
import { createContext, isValidElement, useContext } from "react";
import type { ReactElement, ReactNode } from "react";
import cx from "ui/cx";
import { usePopoverContainer } from "ui/hooks/usePopoverContainer";
import { canHostProps } from "ui/promoteChildren";
import styles from "./Tooltip.module.scss";

type CollisionPadding = number | Partial<Record<"top" | "right" | "bottom" | "left", number>>;

interface TooltipProviderProps {
  children: ReactNode;
  delayDuration?: number;
  skipDelayDuration?: number;
}

/** Details forwarded by Base UI on open/close, including the `reason` for the change. */
type TooltipOpenChangeDetails = TooltipRootChangeEventDetails;

interface TooltipRootProps {
  children: ReactNode;
  defaultOpen?: boolean;
  open?: boolean;
  /**
   * Called on every open and close. `eventDetails.reason` names what caused it, which a controlled
   * consumer needs to tell a dismissal the user asked for ("trigger-press", "escape-key",
   * "outside-press") apart from one the pointer caused by leaving the trigger ("trigger-hover").
   */
  onOpenChange?: (open: boolean, eventDetails: TooltipOpenChangeDetails) => void;
  delayDuration?: number;
  disableHoverableContent?: boolean;
}

interface TooltipTriggerProps extends Omit<React.ComponentProps<typeof BaseTooltip.Trigger>, "render" | "children"> {
  children?: ReactNode;
}

// Extends the Popup's own props so standard HTML attributes (style, data-*, event handlers)
// keep passing through to the rendered element, as they did with the Radix wrapper.
interface TooltipContentProps
  extends Omit<React.ComponentProps<typeof BaseTooltip.Popup>, "render" | "children" | "className"> {
  children?: ReactNode;
  className?: string;
  side?: "top" | "right" | "bottom" | "left";
  sideOffset?: number;
  align?: "start" | "center" | "end";
  alignOffset?: number;
  collisionPadding?: CollisionPadding;
  arrowPadding?: number;
  sticky?: boolean;
  ariaLabel?: string;
  // Portal props
  container?: HTMLElement;
  keepMounted?: boolean;
  // Arrow props
  showArrow?: boolean;
  arrowClassName?: string;
}

interface TooltipArrowProps {
  className?: string;
}

interface TooltipPortalProps {
  children: ReactNode;
  container?: HTMLElement;
  keepMounted?: boolean;
}

/** Radix's default open delay; Base UI's default is 600ms, so set it explicitly for parity. */
const DEFAULT_DELAY_DURATION = 700;

/**
 * Base UI sets the open delay on the Trigger (or Provider), not the Root. Carry the wrapper's
 * `delayDuration` (Root-level first, then Provider-level) down to the Trigger so the public
 * compound API keeps working.
 */
const TooltipDelayContext = createContext<number | undefined>(undefined);

function TooltipProvider({
  children,
  delayDuration = DEFAULT_DELAY_DURATION,
  skipDelayDuration = 300,
}: TooltipProviderProps) {
  return (
    <BaseTooltip.Provider delay={delayDuration} timeout={skipDelayDuration}>
      <TooltipDelayContext.Provider value={delayDuration}>{children}</TooltipDelayContext.Provider>
    </BaseTooltip.Provider>
  );
}

function TooltipRoot({ children, delayDuration, disableHoverableContent, onOpenChange, ...props }: TooltipRootProps) {
  const providerDelay = useContext(TooltipDelayContext);
  return (
    <TooltipDelayContext.Provider value={delayDuration ?? providerDelay}>
      <BaseTooltip.Root disableHoverablePopup={disableHoverableContent} onOpenChange={onOpenChange} {...props}>
        {children}
      </BaseTooltip.Root>
    </TooltipDelayContext.Provider>
  );
}

/**
 * Promote trigger children to Base UI's `render` element. A single React element is used directly
 * (replaces the Radix child-merging token). Non-element children (e.g. a bare string) are wrapped
 * in a <span> first: without a `render` element Base UI renders its own host <button>, which nests
 * invalid/inaccessible markup when the tooltip trigger already sits inside an interactive element
 * (see ConditionsOperatorSelect). Elements that cannot carry props (a fragment) are wrapped the
 * same way — using them as `render` would drop the trigger's ref and handlers, so no tooltip.
 */
function toRenderElement(children: ReactNode): ReactElement | undefined {
  if (isValidElement(children) && canHostProps(children)) {
    return children;
  }
  if (children === null || children === undefined) {
    return undefined;
  }
  return <span>{children}</span>;
}

function TooltipTrigger({ children, className, ...rest }: TooltipTriggerProps) {
  const delay = useContext(TooltipDelayContext) ?? DEFAULT_DELAY_DURATION;
  const renderChild = toRenderElement(children);

  return (
    <BaseTooltip.Trigger
      className={className}
      delay={delay}
      data-testid="tooltip-trigger"
      data-tooltip-trigger=""
      {...rest}
      {...(renderChild ? { render: renderChild } : {})}
    />
  );
}

function TooltipPortal({ children, container, keepMounted }: TooltipPortalProps) {
  return (
    <BaseTooltip.Portal container={container} keepMounted={keepMounted}>
      {children}
    </BaseTooltip.Portal>
  );
}

// Visual height of the arrow. Radix added the arrow height to the offset (`mainAxis: sideOffset +
// arrowHeight`), so `sideOffset` was the gap from the trigger to the arrow tip. Base UI's
// `sideOffset` is the gap to the popup itself, so we add the arrow height back to keep the popup at
// the same distance from the trigger (and stop the arrow overlapping it).
//
// KEEP IN SYNC with `.arrow` in Tooltip.module.scss: the arrow is drawn there as a 10px square
// rotated 45deg and inset -3px, so it protrudes ~4px (7px half-diagonal - 3px inset); 5 matches the
// arrow height Radix measured for the same shape. Resizing the SCSS arrow requires updating this.
const TOOLTIP_ARROW_HEIGHT = 5;

function TooltipContent({
  children,
  className,
  side = "top",
  sideOffset = 3,
  align = "center",
  alignOffset,
  collisionPadding = 12,
  arrowPadding,
  sticky,
  ariaLabel,
  // Portal props
  container,
  keepMounted,
  // Arrow props
  showArrow = true,
  arrowClassName,
  ...rest
}: TooltipContentProps) {
  const popoverContainer = usePopoverContainer();
  const tooltipContainer = container ?? popoverContainer;

  return (
    <BaseTooltip.Portal container={tooltipContainer ?? undefined} keepMounted={keepMounted}>
      <BaseTooltip.Positioner
        side={side}
        sideOffset={showArrow ? sideOffset + TOOLTIP_ARROW_HEIGHT : sideOffset}
        align={align}
        alignOffset={alignOffset}
        collisionPadding={collisionPadding}
        arrowPadding={arrowPadding}
        sticky={sticky}
        // Fixed positioning keeps the popup out of the portal container's scroll size (see
        // Popover.tsx); Base UI defaults to `absolute`.
        positionMethod="fixed"
        className={styles.positioner}
      >
        <BaseTooltip.Popup
          className={cx(styles.content, className)}
          aria-label={ariaLabel}
          data-testid="tooltip-content"
          {...rest}
        >
          {children}
          {showArrow && <BaseTooltip.Arrow className={cx(styles.arrow, arrowClassName)} data-testid="tooltip-arrow" />}
        </BaseTooltip.Popup>
      </BaseTooltip.Positioner>
    </BaseTooltip.Portal>
  );
}

function TooltipArrow({ className }: TooltipArrowProps) {
  return <BaseTooltip.Arrow className={cx(styles.arrow, className)} data-testid="tooltip-arrow" />;
}

// Compound component exports
export const Tooltip = {
  Provider: TooltipProvider,
  Root: TooltipRoot,
  Trigger: TooltipTrigger,
  Portal: TooltipPortal,
  Content: TooltipContent,
  Arrow: TooltipArrow,
};

// Type exports
export type {
  TooltipProviderProps,
  TooltipRootProps,
  TooltipTriggerProps,
  TooltipContentProps,
  TooltipArrowProps,
  TooltipPortalProps,
  TooltipOpenChangeDetails,
};

export default Tooltip;
